import React, { useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { RolMasivo, UsuarioMasivoItem, UsuarioMasivoResponse } from 'types/usuario.types';
import { usuarioService } from 'services/usuarioService';
import { getConfig } from 'utils/appConfig';

// ============================================================
// RegistroMasivoPage – Registro masivo de ESTUDIANTE / DOCENTE
// username  = DNI + 2 primeros chars del rol (mayúscula)  → 12345678ES
// password  = 3 primeros chars del rol (minúscula) + _ + DNI → est_12345678
// ============================================================

const ROLES: RolMasivo[] = ['ESTUDIANTE', 'DOCENTE'];

const ROL_COLOR: Record<RolMasivo, string> = {
  ESTUDIANTE: 'bg-blue-100 text-blue-700',
  DOCENTE:    'bg-purple-100 text-purple-700',
};

// ── Fórmulas de credenciales ──────────────────────────────────
const generarUsername = (dni: string, rol: RolMasivo) =>
  `${dni}${rol.substring(0, 2).toUpperCase()}`;

const generarPassword = (dni: string, rol: RolMasivo) =>
  `${rol.substring(0, 3).toLowerCase()}_${dni}`;

// ── Íconos ────────────────────────────────────────────────────
const Ico = ({ d, cls = 'w-4 h-4' }: { d: string; cls?: string }) => (
  <svg viewBox="0 0 20 20" fill="currentColor" className={cls}><path d={d} /></svg>
);
const D_PLUS     = 'M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z';
const D_DEL      = 'M8.75 1A2.75 2.75 0 0 0 6 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 1 0 .23 1.482l.149-.022.841 10.518A2.75 2.75 0 0 0 7.596 19h4.807a2.75 2.75 0 0 0 2.742-2.53l.841-10.52.149.023a.75.75 0 0 0 .23-1.482A41.03 41.03 0 0 0 14 4.193V3.75A2.75 2.75 0 0 0 11.25 1h-2.5ZM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4ZM8.58 7.72a.75.75 0 0 0-1.5.06l.3 7.5a.75.75 0 1 0 1.5-.06l-.3-7.5Zm4.34.06a.75.75 0 1 0-1.5-.06l-.3 7.5a.75.75 0 1 0 1.5.06l.3-7.5Z';
const D_UPLOAD   = 'M9.25 13.25a.75.75 0 0 0 1.5 0V4.636l2.955 3.129a.75.75 0 0 0 1.09-1.03l-4.25-4.5a.75.75 0 0 0-1.09 0l-4.25 4.5a.75.75 0 1 0 1.09 1.03L9.25 4.636v8.614Z';
const D_SEND     = 'M3.105 2.288a.75.75 0 0 0-.826.95l1.414 4.926A1.5 1.5 0 0 0 5.135 9.25h6.115a.75.75 0 0 1 0 1.5H5.135a1.5 1.5 0 0 0-1.442 1.086l-1.414 4.926a.75.75 0 0 0 .826.95 28.897 28.897 0 0 0 15.293-7.154.75.75 0 0 0 0-1.115A28.897 28.897 0 0 0 3.105 2.288Z';
const D_CLEAR    = 'M8.75 1A2.75 2.75 0 0 0 6 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 1 0 .23 1.482l.149-.022.841 10.518A2.75 2.75 0 0 0 7.596 19h4.807a2.75 2.75 0 0 0 2.742-2.53l.841-10.52.149.023a.75.75 0 0 0 .23-1.482A41.03 41.03 0 0 0 14 4.193V3.75A2.75 2.75 0 0 0 11.25 1h-2.5ZM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4Z';
const D_CHECK    = 'M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z';
const D_WARN     = 'M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495ZM10 5a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0v-3.5A.75.75 0 0 1 10 5Zm0 9a1 1 0 1 1 0-2 1 1 0 0 1 0 2Z';
const D_DOWNLOAD = 'M10.75 2.75a.75.75 0 0 0-1.5 0v8.614L6.295 8.235a.75.75 0 1 0-1.09 1.03l4.25 4.5a.75.75 0 0 0 1.09 0l4.25-4.5a.75.75 0 0 0-1.09-1.03l-2.955 3.129V2.75Z';
const D_INFO     = 'M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-7-4a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM9 9a.75.75 0 0 0 0 1.5h.253a.25.25 0 0 1 .244.304l-.459 2.066A1.75 1.75 0 0 0 10.747 15H11a.75.75 0 0 0 0-1.5h-.253a.25.25 0 0 1-.244-.304l.459-2.066A1.75 1.75 0 0 0 9.253 9H9Z';

const Spin = () => (
  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
  </svg>
);

// ── Fila vacía ─────────────────────────────────────────────────
const filaVacia = (): UsuarioMasivoItem => ({
  username: '', password: '', nombres: '', apellidos: '',
  email: '', dni: '', rol: 'ESTUDIANTE', codigoSiagie: null,
  sexo: null,
});

/** Normaliza el valor del Excel/celda al enum del backend ('M' | 'F' | null). */
const normalizarSexo = (raw: string): 'M' | 'F' | null => {
  const v = raw.trim().toUpperCase();
  if (v === 'M' || v === 'MASCULINO' || v === 'H' || v === 'HOMBRE') return 'M';
  if (v === 'F' || v === 'FEMENINO'  || v === 'MUJER')               return 'F';
  return null;
};

// ── Aplica las fórmulas a una fila (si el DNI tiene 8 dígitos) ─
const completarCredenciales = (f: UsuarioMasivoItem): UsuarioMasivoItem => {
  if (f.dni.length === 8) {
    return {
      ...f,
      username: generarUsername(f.dni, f.rol),
      password: generarPassword(f.dni, f.rol),
    };
  }
  return f;
};

// ── Validar fila ───────────────────────────────────────────────
const validarFila = (f: UsuarioMasivoItem): string[] => {
  const e: string[] = [];
  if (!f.nombres.trim())   e.push('Nombres requeridos');
  if (!f.apellidos.trim()) e.push('Apellidos requeridos');
  if (!f.dni.trim())       e.push('DNI requerido');
  if (f.dni && !/^\d{8}$/.test(f.dni)) e.push('DNI debe tener 8 dígitos');
  if (!f.email.trim()) {
    e.push('Email requerido');
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) {
    e.push('Email inválido');
  } else {
    const dominio = getConfig().correodominio || '@gmail.com';
    if (dominio !== '@gmail.com' && !f.email.endsWith(dominio)) {
      e.push(`El email debe terminar con ${dominio}`);
    }
  }
  if (f.rol === 'ESTUDIANTE' && f.sexo !== 'M' && f.sexo !== 'F') {
    e.push('Sexo requerido (M o F)');
  }
  return e;
};

// ── Columnas visibles de la tabla (sin username/password) ──────
const COLS = [
  { key: 'nombres',      label: 'Nombres',     w: 'min-w-[140px]' },
  { key: 'apellidos',    label: 'Apellidos',   w: 'min-w-[140px]' },
  { key: 'dni',          label: 'DNI',         w: 'min-w-[95px]'  },
  { key: 'email',        label: 'Email',       w: 'min-w-[170px]' },
  { key: 'sexo',         label: 'Sexo',        w: 'min-w-[100px]' },
  { key: 'codigoSiagie', label: 'Cód. SIAGIE', w: 'min-w-[120px]' },
] as const;

type ColKey = typeof COLS[number]['key'];

// ── Mapeo de columnas Excel → campos ──────────────────────────
const EXCEL_MAP: Record<string, ColKey | 'rol'> = {
  nombres:       'nombres',      name:        'nombres',
  apellidos:     'apellidos',    lastname:    'apellidos',  surname:       'apellidos',
  dni:           'dni',
  email:         'email',        correo:      'email',
  codigosiagie:  'codigoSiagie', siagie:      'codigoSiagie', 'cod. siagie': 'codigoSiagie',
  rol:           'rol',          role:        'rol',
  sexo:          'sexo',         genero:      'sexo',         'sexo (m/f)':  'sexo',
};

// ─────────────────────────────────────────────────────────────
const RegistroMasivoPage: React.FC = () => {
  const [filas, setFilas]             = useState<UsuarioMasivoItem[]>([filaVacia()]);
  const [erroresFila, setErroresFila] = useState<Record<number, string[]>>({});
  const [enviando, setEnviando]       = useState(false);
  const [resultado, setResultado]     = useState<UsuarioMasivoResponse | null>(null);
  const [errorEnvio, setErrorEnvio]   = useState('');
  const fileRef                       = useRef<HTMLInputElement>(null);

  // ── Editar celda ────────────────────────────────────────────
  const setCell = (i: number, key: ColKey | 'rol', val: string) => {
    setFilas(prev => prev.map((f, idx) => {
      if (idx !== i) return f;
      let updated: UsuarioMasivoItem;
      if (key === 'rol') {
        const rol = val as RolMasivo;
        updated = { ...f, rol, codigoSiagie: rol !== 'ESTUDIANTE' ? null : f.codigoSiagie };
      } else if (key === 'sexo') {
        // Dropdown: '' = null; 'M'/'F' = enum literal
        const v = val as '' | 'M' | 'F';
        updated = { ...f, sexo: v === '' ? null : v };
      } else {
        updated = { ...f, [key]: key === 'codigoSiagie' ? (val || null) : val };
      }
      // Regenerar credenciales automáticamente si cambia DNI o rol
      return completarCredenciales(updated);
    }));
    if (erroresFila[i]) setErroresFila(prev => { const n = { ...prev }; delete n[i]; return n; });
  };

  // ── Agregar / eliminar / limpiar ─────────────────────────────
  const agregarFila  = () => setFilas(prev => [...prev, filaVacia()]);
  const eliminarFila = (i: number) => setFilas(prev => prev.filter((_, idx) => idx !== i));
  const limpiarTabla = () => { setFilas([filaVacia()]); setErroresFila({}); setResultado(null); setErrorEnvio(''); };

  // ── Cargar Excel ─────────────────────────────────────────────
  const handleExcel = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      try {
        const wb   = XLSX.read(ev.target?.result, { type: 'array' });
        const ws   = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json<Record<string, string>>(ws, { defval: '' });

        const parsed: UsuarioMasivoItem[] = rows.map(row => {
          const fila = filaVacia();
          Object.entries(row).forEach(([col, val]) => {
            const mapped = EXCEL_MAP[col.toLowerCase().trim()];
            if (mapped === 'rol') {
              const v = String(val).toUpperCase();
              if (v === 'ESTUDIANTE' || v === 'DOCENTE') fila.rol = v as RolMasivo;
            } else if (mapped === 'sexo') {
              fila.sexo = normalizarSexo(String(val));
            } else if (mapped) {
              (fila as any)[mapped] = String(val).trim();
            }
          });
          // Auto-generar credenciales desde los datos importados
          return completarCredenciales(fila);
        }).filter(f => f.nombres || f.apellidos || f.dni);

        if (parsed.length > 0) {
          setFilas(parsed);
          setErroresFila({});
          setResultado(null);
          setErrorEnvio('');
        }
      } catch {
        setErrorEnvio('No se pudo leer el archivo Excel. Verifica el formato.');
      }
      if (fileRef.current) fileRef.current.value = '';
    };
    reader.readAsArrayBuffer(file);
  };

  // ── Descargar plantilla Excel ─────────────────────────────────
  const descargarPlantilla = () => {
    const dominio = getConfig().correodominio || '@gmail.com';
    const ws = XLSX.utils.aoa_to_sheet([
      ['nombres', 'apellidos', 'dni', 'email', 'rol', 'sexo', 'codigoSiagie'],
      ['Juan',  'Pérez García', '12345678', `jperez${dominio}`, 'DOCENTE',    'M', ''],
      ['María', 'López Ríos',   '87654321', `mlopez${dominio}`, 'ESTUDIANTE', 'F', 'S123456'],
    ]);
    ws['!cols'] = [
      { wch: 20 }, { wch: 20 }, { wch: 12 }, { wch: 32 }, { wch: 14 }, { wch: 6 }, { wch: 14 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Usuarios');
    XLSX.writeFile(wb, 'plantilla_registro_masivo.xlsx');
  };

  // ── Enviar ───────────────────────────────────────────────────
  const handleEnviar = async () => {
    const nuevosErrores: Record<number, string[]> = {};
    filas.forEach((f, i) => {
      const errs = validarFila(f);
      if (errs.length) nuevosErrores[i] = errs;
    });
    if (Object.keys(nuevosErrores).length > 0) {
      setErroresFila(nuevosErrores);
      return;
    }

    setEnviando(true);
    setErrorEnvio('');
    setResultado(null);
    try {
      const payload = filas.map(f => ({
        ...f,
        codigoSiagie: f.rol === 'ESTUDIANTE' ? (f.codigoSiagie?.trim() || null) : null,
      }));
      const res = await usuarioService.registrarMasivo(payload);
      setResultado(res);
      if (res.totalErrores === 0) setFilas([filaVacia()]);
    } catch (err: any) {
      setErrorEnvio(err.message ?? 'Error al registrar usuarios');
    } finally { setEnviando(false); }
  };

  const hayErroresFila = Object.keys(erroresFila).length > 0;

  return (
    <div className="space-y-5">

      {/* Cabecera */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Registro Masivo de Usuarios</h1>
          <p className="text-slate-400 text-sm mt-0.5">
            Roles permitidos:{' '}
            {ROLES.map(r => (
              <span key={r} className={`inline-block px-2 py-0.5 rounded-full font-bold text-xs mr-1 ${ROL_COLOR[r]}`}>{r}</span>
            ))}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={descargarPlantilla}
            className="flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200
                       text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors">
            <Ico d={D_DOWNLOAD} />
            Plantilla Excel
          </button>
          <button onClick={() => fileRef.current?.click()}
            className="flex items-center gap-2 px-3 py-2 rounded-xl border border-green-200
                       text-sm font-semibold text-green-700 hover:bg-green-50 transition-colors">
            <Ico d={D_UPLOAD} />
            Cargar Excel
          </button>
          <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleExcel} />
          {filas.length > 1 && (
            <button onClick={limpiarTabla}
              className="flex items-center gap-2 px-3 py-2 rounded-xl border border-red-200
                         text-sm font-semibold text-red-600 hover:bg-red-50 transition-colors">
              <Ico d={D_CLEAR} />
              Limpiar
            </button>
          )}
        </div>
      </div>

      {/* Panel informativo de credenciales */}
      <div className="bg-blue-50 border border-blue-200 rounded-2xl px-5 py-4 flex items-start gap-3">
        <Ico d={D_INFO} cls="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
        <div className="space-y-1.5 text-sm">
          <p className="font-semibold text-blue-800">Credenciales generadas automáticamente</p>
          <div className="flex flex-wrap gap-x-8 gap-y-1 text-blue-700">
            <span>
              <span className="font-medium">Usuario:</span>{' '}
              <code className="bg-blue-100 px-1.5 py-0.5 rounded text-xs font-mono">
                DNI + 2 primeros chars del rol
              </code>
              <span className="text-blue-500 text-xs ml-2">Ej: 12345678ES · 12345678DO</span>
            </span>
            <span>
              <span className="font-medium">Contraseña:</span>{' '}
              <code className="bg-blue-100 px-1.5 py-0.5 rounded text-xs font-mono">
                3 chars del rol + _ + DNI
              </code>
              <span className="text-blue-500 text-xs ml-2">Ej: est_12345678 · doc_12345678</span>
            </span>
          </div>
          <p className="text-blue-500 text-xs">
            Usuario y contraseña se calculan automáticamente al ingresar el DNI.
            El email es obligatorio y debe ingresarse manualmente
            {getConfig().correodominio && getConfig().correodominio !== '@gmail.com'
              ? ` con el dominio ${getConfig().correodominio}`
              : ''}.
            El archivo Excel requiere: nombres, apellidos, DNI, email, rol y código SIAGIE.
          </p>
        </div>
      </div>

      {/* Resultado */}
      {resultado && (
        <div className={`rounded-2xl border p-5 space-y-4
          ${resultado.totalErrores === 0 ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-xl bg-slate-100 flex items-center justify-center">
                <Ico d={D_SEND} cls="w-4 h-4 text-slate-500" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Enviados</p>
                <p className="text-lg font-bold text-slate-800 tabular-nums">{resultado.totalEnviados}</p>
              </div>
            </div>
            <div className="w-px h-10 bg-slate-200"/>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-xl bg-green-100 flex items-center justify-center">
                <Ico d={D_CHECK} cls="w-4 h-4 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-green-600">Creados</p>
                <p className="text-lg font-bold text-green-700 tabular-nums">{resultado.totalCreados}</p>
              </div>
            </div>
            <div className="w-px h-10 bg-slate-200"/>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-xl bg-red-100 flex items-center justify-center">
                <Ico d={D_WARN} cls="w-4 h-4 text-red-500" />
              </div>
              <div>
                <p className="text-xs text-red-500">Errores</p>
                <p className="text-lg font-bold text-red-600 tabular-nums">{resultado.totalErrores}</p>
              </div>
            </div>
          </div>
          {resultado.errores.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-bold text-amber-700 uppercase tracking-wide">Filas con error</p>
              {resultado.errores.map((err, i) => (
                <div key={i} className="flex items-start gap-2 bg-white rounded-xl px-4 py-2.5
                                       border border-red-100 text-sm">
                  <span className="text-xs font-bold text-red-500 bg-red-50 px-2 py-0.5 rounded-lg flex-shrink-0 mt-0.5">
                    Fila {err.fila}
                  </span>
                  <span className="text-slate-600 flex-1">
                    <span className="font-semibold text-slate-800">{err.username}</span>
                    {' — '}{err.motivo}
                  </span>
                </div>
              ))}
            </div>
          )}
          {resultado.totalErrores === 0 && (
            <p className="text-green-700 font-semibold text-sm flex items-center gap-2">
              <Ico d={D_CHECK} cls="w-4 h-4" />
              Todos los usuarios fueron creados exitosamente
            </p>
          )}

          {/* Aviso para docentes: el sexo solo quedó en Usuario.sexo */}
          {(() => {
            const docentesConSexo = resultado.creados.filter(
              u => u.rol === 'DOCENTE' && (u.sexo === 'M' || u.sexo === 'F'),
            );
            if (docentesConSexo.length === 0) return null;
            return (
              <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex items-start gap-2 text-sm">
                <Ico d={D_INFO} cls="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <div className="text-blue-800">
                  <p className="font-semibold mb-0.5">
                    Completa los datos profesionales de {docentesConSexo.length} docente{docentesConSexo.length !== 1 ? 's' : ''}
                  </p>
                  <p className="text-xs text-blue-700 leading-snug">
                    El sexo se guardó en el perfil de usuario, pero no en la ficha profesional. Para que aparezca en el ROD y otros documentos
                    oficiales, abre <span className="font-semibold">Usuarios → Docentes → Datos prof.</span> de cada docente y guarda el formulario.
                  </p>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* Error de envío */}
      {errorEnvio && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700 flex items-center gap-2">
          <Ico d={D_WARN} />
          {errorEnvio}
        </div>
      )}

      {/* Advertencia validación */}
      {hayErroresFila && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-700 flex items-center gap-2">
          <Ico d={D_WARN} />
          Corrige los errores resaltados antes de enviar
        </div>
      )}

      {/* Tabla de edición */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50">
                <th className="px-3 py-3 text-xs font-bold text-slate-400 uppercase tracking-wide text-center w-10">#</th>
                {COLS.map(c => (
                  <th key={c.key} className={`px-3 py-3 text-left text-xs font-bold text-slate-400 uppercase tracking-wide ${c.w}`}>
                    {c.label}
                    {['nombres','apellidos','dni','email'].includes(c.key) && (
                      <span className="text-red-400 ml-0.5">*</span>
                    )}
                  </th>
                ))}
                <th className="px-3 py-3 text-left text-xs font-bold text-slate-400 uppercase tracking-wide min-w-[120px]">
                  Rol <span className="text-red-400">*</span>
                </th>
                {/* Columnas generadas (solo lectura) */}
                <th className="px-3 py-3 text-left text-xs font-bold text-slate-300 uppercase tracking-wide min-w-[140px]">
                  Usuario <span className="text-slate-300 font-normal normal-case">(auto)</span>
                </th>
                <th className="px-3 py-3 text-left text-xs font-bold text-slate-300 uppercase tracking-wide min-w-[150px]">
                  Contraseña <span className="text-slate-300 font-normal normal-case">(auto)</span>
                </th>
                <th className="w-10"/>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {filas.map((fila, i) => {
                const errs = erroresFila[i] ?? [];
                return (
                  <React.Fragment key={i}>
                    <tr className={errs.length > 0 ? 'bg-red-50/40' : 'hover:bg-slate-50/60'}>
                      <td className="px-3 py-2 text-center">
                        <span className="text-xs text-slate-400 font-semibold tabular-nums">{i + 1}</span>
                      </td>

                      {/* Campos editables */}
                      {COLS.map(c => {
                        const isSiagie = c.key === 'codigoSiagie';
                        const isSexo   = c.key === 'sexo';
                        const disabled = isSiagie && fila.rol !== 'ESTUDIANTE';
                        if (isSexo) {
                          return (
                            <td key={c.key} className="px-2 py-2">
                              <select
                                value={fila.sexo ?? ''}
                                onChange={e => setCell(i, c.key, e.target.value)}
                                className={`w-full px-2.5 py-1.5 rounded-lg border text-sm outline-none
                                           focus:ring-2 focus:ring-blue-400 bg-white transition-all
                                           ${errs.length ? 'border-red-300' : 'border-slate-200'}`}
                              >
                                <option value="">—</option>
                                <option value="M">Masculino</option>
                                <option value="F">Femenino</option>
                              </select>
                            </td>
                          );
                        }
                        const cellValue = isSiagie
                          ? (fila.codigoSiagie ?? '')
                          : fila[c.key as Exclude<ColKey, 'codigoSiagie' | 'sexo'>];
                        return (
                          <td key={c.key} className="px-2 py-2">
                            <input
                              type="text"
                              value={cellValue}
                              onChange={e => setCell(i, c.key, e.target.value)}
                              placeholder={disabled ? 'N/A' : c.label}
                              disabled={disabled}
                              className={`w-full px-2.5 py-1.5 rounded-lg border text-sm outline-none
                                         focus:ring-2 focus:ring-blue-400 transition-all
                                         ${disabled
                                           ? 'bg-slate-100 text-slate-400 border-slate-100 cursor-not-allowed'
                                           : errs.length ? 'border-red-300 bg-white' : 'border-slate-200 bg-white'
                                         }`}
                            />
                          </td>
                        );
                      })}

                      {/* Rol */}
                      <td className="px-2 py-2">
                        <select value={fila.rol}
                          onChange={e => setCell(i, 'rol', e.target.value)}
                          className="w-full px-2.5 py-1.5 rounded-lg border border-slate-200 text-sm
                                     outline-none focus:ring-2 focus:ring-blue-400 bg-white transition-all">
                          {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                        </select>
                      </td>

                      {/* Username generado (solo lectura) */}
                      <td className="px-2 py-2">
                        <span className={`block px-2.5 py-1.5 rounded-lg text-sm font-mono
                          ${fila.username
                            ? 'bg-slate-50 text-slate-500 border border-slate-100'
                            : 'text-slate-300 text-xs italic'}`}>
                          {fila.username || '—  ingresa DNI'}
                        </span>
                      </td>

                      {/* Password generado (solo lectura) */}
                      <td className="px-2 py-2">
                        <span className={`block px-2.5 py-1.5 rounded-lg text-sm font-mono
                          ${fila.password
                            ? 'bg-slate-50 text-slate-500 border border-slate-100'
                            : 'text-slate-300 text-xs italic'}`}>
                          {fila.password || '—'}
                        </span>
                      </td>

                      {/* Eliminar */}
                      <td className="px-2 py-2">
                        {filas.length > 1 && (
                          <button onClick={() => eliminarFila(i)}
                            className="p-1.5 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors">
                            <Ico d={D_DEL} />
                          </button>
                        )}
                      </td>
                    </tr>

                    {errs.length > 0 && (
                      <tr>
                        <td colSpan={COLS.length + 5} className="px-4 pb-2 pt-0 bg-red-50/40">
                          <p className="text-xs text-red-500">{errs.join(' · ')}</p>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="px-4 py-3 border-t border-slate-100 bg-slate-50 flex items-center justify-between gap-3">
          <button onClick={agregarFila}
            className="flex items-center gap-1.5 text-sm font-semibold text-blue-700
                       hover:text-blue-900 transition-colors">
            <Ico d={D_PLUS} />
            Agregar fila
          </button>
          <span className="text-xs text-slate-400">{filas.length} usuario{filas.length !== 1 ? 's' : ''} a registrar</span>
        </div>
      </div>

      {/* Botón enviar */}
      <div className="flex justify-end">
        <button onClick={handleEnviar} disabled={enviando || filas.length === 0}
          className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-blue-700 hover:bg-blue-800
                     text-white font-semibold text-sm transition-colors shadow-sm
                     disabled:opacity-60 disabled:cursor-not-allowed">
          {enviando ? <Spin /> : <Ico d={D_SEND} />}
          {enviando ? 'Registrando...' : `Registrar ${filas.length} usuario${filas.length !== 1 ? 's' : ''}`}
        </button>
      </div>

    </div>
  );
};

export default RegistroMasivoPage;
